import { useState } from 'react';
import { useMyProfile } from '../../hooks/useMyProfile';
import { useStaffUsers } from '../../hooks/useStaffUsers';
import {
  useSetBillingOutcome,
  useSetBillingTerms,
  useTransitionTrip,
  useTripBilling,
  useUpdateTrip,
  type TripBillingOverview,
} from '../../hooks/useTripBilling';
import { formatCurrency, formatDate } from '../../lib/formatters';
import {
  BASIS_LABELS,
  BILLING_STATUS_LABELS,
  billingWarnings,
  canRecordInvoice,
  canSetBillingTerms,
  parseDollarsToCents,
  toNumber,
  tripActions,
  type BillingBasis,
  type BillingStatus,
  type TripStatus,
} from '../../lib/tripLifecycle';

export const inputClass =
  'w-full rounded border border-border bg-surface px-2 py-1 min-h-[40px] text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary';
const btn =
  'rounded px-3 py-1.5 min-h-[40px] text-xs font-medium disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';
const toneClass = {
  primary: `${btn} bg-primary hover:bg-primary-hover text-text-inverse`,
  neutral: `${btn} bg-surface-3 hover:bg-surface-strong text-text`,
  danger: `${btn} bg-notification-soft hover:bg-surface-strong text-notification`,
} as const;

const BILLING_TONE: Record<BillingStatus, string> = {
  pending: 'bg-surface-3 text-text-muted',
  ready: 'bg-warning-soft text-warning',
  invoiced: 'bg-success-soft text-success',
  not_billable: 'bg-surface-3 text-text-faint',
  unreconciled: 'bg-info-soft text-info',
};

export function BillingStatusPill({ status }: { status: BillingStatus }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${BILLING_TONE[status]}`}>
      {BILLING_STATUS_LABELS[status]}
    </span>
  );
}

export function formatHours(v: number | string | null | undefined): string {
  const n = toNumber(v);
  return n == null ? 'Not recorded' : `${n.toLocaleString('en-US', { maximumFractionDigits: 2 })} h`;
}

export function formatEstimate(row: Pick<TripBillingOverview, 'estimated_amount_cents' | 'billing_basis'>): string {
  const cents = toNumber(row.estimated_amount_cents);
  if (cents != null) return formatCurrency(cents);
  if (row.billing_basis === 'project') return 'Per project / PO';
  if (row.billing_basis === 'contract') return 'Covered by contract';
  return '—';
}

function ErrorLine({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="text-xs text-notification">
      {error instanceof Error ? error.message : String(error)}
    </p>
  );
}

// ─── Status actions ─────────────────────────────────────────────────────────

export function TripStatusActions({
  tripId,
  status,
  billingStatus,
}: {
  tripId: string;
  status: TripStatus;
  billingStatus: BillingStatus | null;
}) {
  const transition = useTransitionTrip();
  const actions = tripActions(status, billingStatus);
  if (actions.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => (
          <button
            key={a.to}
            type="button"
            disabled={transition.isPending}
            className={toneClass[a.tone]}
            onClick={() => {
              if (a.confirm && !window.confirm(a.confirm)) return;
              transition.mutate({ tripId, to: a.to });
            }}
          >
            {a.label}
          </button>
        ))}
      </div>
      {status === 'completed' && billingStatus === 'invoiced' && (
        <p className="text-xs text-text-muted">Invoiced trips cannot be reopened until billing clears the invoice.</p>
      )}
      <ErrorLine error={transition.error} />
    </div>
  );
}

// ─── Reschedule / reassign ──────────────────────────────────────────────────

export function TripEditForm({
  trip,
  onDone,
}: {
  trip: {
    id: string;
    start_date: string | null;
    end_date: string | null;
    lead_technician_id: string | null;
    scope: string | null;
    po_number: string | null;
  };
  onDone: () => void;
}) {
  const { data: staff } = useStaffUsers();
  const update = useUpdateTrip();
  const [start, setStart] = useState(trip.start_date ?? '');
  const [end, setEnd] = useState(trip.end_date ?? trip.start_date ?? '');
  const [lead, setLead] = useState(trip.lead_technician_id ?? '');
  const [scope, setScope] = useState(trip.scope ?? '');
  const [po, setPo] = useState(trip.po_number ?? '');
  const invalid = !start || !end || end < start;

  return (
    <form
      className="space-y-3 rounded-lg border border-border bg-surface-2 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (invalid) return;
        update.mutate(
          {
            tripId: trip.id,
            patch: {
              start_date: start,
              end_date: end,
              lead_technician_id: lead || null,
              scope: scope.trim() || null,
              po_number: po.trim() || null,
            },
          },
          { onSuccess: onDone },
        );
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-text-muted space-y-1">
          <span>Start</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inputClass} required />
        </label>
        <label className="text-xs text-text-muted space-y-1">
          <span>End</span>
          <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} className={inputClass} required />
        </label>
      </div>
      {end && start && end < start && <p className="text-xs text-notification">The end date is before the start date.</p>}
      <label className="block text-xs text-text-muted space-y-1">
        <span>Lead technician</span>
        <select value={lead} onChange={(e) => setLead(e.target.value)} className={inputClass}>
          <option value="">Unassigned</option>
          {(staff ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.full_name ?? u.email}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs text-text-muted space-y-1">
        <span>PO number</span>
        <input value={po} onChange={(e) => setPo(e.target.value)} className={inputClass} autoComplete="off" />
      </label>
      <label className="block text-xs text-text-muted space-y-1">
        <span>Scope</span>
        <textarea value={scope} onChange={(e) => setScope(e.target.value)} rows={3} className={inputClass} />
      </label>
      <ErrorLine error={update.error} />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className={toneClass.neutral}>
          Cancel
        </button>
        <button type="submit" disabled={invalid || update.isPending} className={toneClass.primary}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

// ─── Billing ────────────────────────────────────────────────────────────────

export function TripBillingPanel({ tripId }: { tripId: string }) {
  const { data: row, isLoading, isError } = useTripBilling(tripId);
  const { data: me } = useMyProfile();
  const [editing, setEditing] = useState(false);

  if (isLoading) return <p className="text-xs text-text-muted" role="status">Loading billing…</p>;
  if (isError || !row) return <p className="text-xs text-notification">Billing details could not be loaded.</p>;

  const warnings = row.billing_status === 'ready' ? billingWarnings(row) : [];
  const mayEditTerms = canSetBillingTerms(me?.role) && row.billing_status !== 'invoiced';

  return (
    <section aria-labelledby={`billing-${tripId}`} className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 id={`billing-${tripId}`} className="text-sm font-semibold text-text">
          Billing
        </h3>
        <BillingStatusPill status={row.billing_status} />
      </div>

      <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-sm">
        <dt className="text-xs text-text-muted pt-0.5">Billed as</dt>
        <dd className="text-text">
          {row.billing_basis ? BASIS_LABELS[row.billing_basis] : <span className="text-warning">Not set</span>}
          {row.billing_basis === 'hourly' && row.hourly_rate_cents != null && ` · ${formatCurrency(row.hourly_rate_cents)}/h`}
          {row.billing_basis === 'flat' && row.flat_fee_cents != null && ` · ${formatCurrency(row.flat_fee_cents)}`}
        </dd>
        <dt className="text-xs text-text-muted pt-0.5">Labor hours</dt>
        <dd className="text-text">
          {formatHours(row.labor_hours)}
          <span className="text-text-faint"> · {row.submitted_report_count} of {row.report_count} reports submitted</span>
        </dd>
        <dt className="text-xs text-text-muted pt-0.5">Estimate</dt>
        <dd className="text-text">{formatEstimate(row)}</dd>
        {row.invoice_reference && (
          <>
            <dt className="text-xs text-text-muted pt-0.5">Invoice</dt>
            <dd className="text-text font-mono">
              {row.invoice_reference}
              {row.invoiced_at && <span className="font-sans text-text-faint"> · {formatDate(row.invoiced_at)}</span>}
            </dd>
          </>
        )}
        {row.billing_notes && (
          <>
            <dt className="text-xs text-text-muted pt-0.5">Notes</dt>
            <dd className="text-text whitespace-pre-wrap break-words">{row.billing_notes}</dd>
          </>
        )}
      </dl>

      {warnings.length > 0 && (
        <ul className="rounded border border-warning bg-warning-soft px-3 py-2 text-xs text-warning space-y-0.5">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}

      {editing ? (
        <BillingTermsForm row={row} onDone={() => setEditing(false)} />
      ) : (
        mayEditTerms && (
          <button type="button" onClick={() => setEditing(true)} className={toneClass.neutral}>
            {row.billing_basis ? 'Change billing terms' : 'Set billing terms'}
          </button>
        )
      )}

      {canRecordInvoice(me?.role) && <BillingOutcomeControls row={row} />}
    </section>
  );
}

export function BillingTermsForm({ row, onDone }: { row: TripBillingOverview; onDone: () => void }) {
  const save = useSetBillingTerms();
  const [basis, setBasis] = useState<BillingBasis>(row.billing_basis ?? 'hourly');
  const [rate, setRate] = useState(row.hourly_rate_cents != null ? (row.hourly_rate_cents / 100).toFixed(2) : '');
  const [fee, setFee] = useState(row.flat_fee_cents != null ? (row.flat_fee_cents / 100).toFixed(2) : '');
  const [notes, setNotes] = useState(row.billing_notes ?? '');
  const rateCents = parseDollarsToCents(rate);
  const feeCents = parseDollarsToCents(fee);
  const amountInvalid =
    (basis === 'hourly' && Number.isNaN(rateCents)) || (basis === 'flat' && Number.isNaN(feeCents));

  return (
    <form
      className="space-y-3 rounded-lg border border-border bg-surface-2 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (amountInvalid) return;
        save.mutate(
          {
            tripId: row.trip_id,
            basis,
            hourlyRateCents: basis === 'hourly' ? rateCents : null,
            flatFeeCents: basis === 'flat' ? feeCents : null,
            notes: notes.trim() || null,
          },
          { onSuccess: onDone },
        );
      }}
    >
      <fieldset className="space-y-1.5">
        <legend className="text-xs text-text-muted mb-1">How is this trip billed?</legend>
        {(Object.keys(BASIS_LABELS) as BillingBasis[]).map((b) => (
          <label key={b} className="flex items-center gap-2 text-sm text-text min-h-[32px]">
            <input type="radio" name={`basis-${row.trip_id}`} value={b} checked={basis === b} onChange={() => setBasis(b)} />
            {BASIS_LABELS[b]}
          </label>
        ))}
      </fieldset>
      {basis === 'hourly' && (
        <label className="block text-xs text-text-muted space-y-1">
          <span>Hourly rate (USD)</span>
          <input inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="125.00" className={inputClass} />
        </label>
      )}
      {basis === 'flat' && (
        <label className="block text-xs text-text-muted space-y-1">
          <span>Flat fee (USD)</span>
          <input inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="950.00" className={inputClass} />
        </label>
      )}
      {amountInvalid && <p className="text-xs text-notification">Enter an amount like 125 or 125.50.</p>}
      <label className="block text-xs text-text-muted space-y-1">
        <span>Billing notes (staff only)</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputClass} />
      </label>
      <p className="text-xs text-text-faint">New trips for this customer start with these terms.</p>
      <ErrorLine error={save.error} />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className={toneClass.neutral}>
          Cancel
        </button>
        <button type="submit" disabled={amountInvalid || save.isPending} className={toneClass.primary}>
          {save.isPending ? 'Saving…' : 'Save terms'}
        </button>
      </div>
    </form>
  );
}

/** Record the Zoho Books invoice number, mark not billable, or undo either. */
export function BillingOutcomeControls({ row, compact = false }: { row: TripBillingOverview; compact?: boolean }) {
  const outcome = useSetBillingOutcome();
  const [invoice, setInvoice] = useState('');

  if (row.trip_status !== 'completed') return null;

  if (row.billing_status === 'invoiced' || row.billing_status === 'not_billable') {
    return (
      <div className="space-y-1.5">
        <button
          type="button"
          disabled={outcome.isPending}
          className={toneClass.neutral}
          onClick={() => {
            const msg =
              row.billing_status === 'invoiced'
                ? `Clear invoice ${row.invoice_reference ?? ''} and move this trip back to ready to bill?`
                : 'Move this trip back to ready to bill?';
            if (window.confirm(msg)) outcome.mutate({ tripId: row.trip_id, status: 'ready' });
          }}
        >
          {row.billing_status === 'invoiced' ? 'Clear invoice' : 'Back to ready to bill'}
        </button>
        <ErrorLine error={outcome.error} />
      </div>
    );
  }

  if (!row.billing_basis && row.billing_status !== 'unreconciled') {
    return compact ? null : <p className="text-xs text-text-muted">Set billing terms before recording an invoice.</p>;
  }

  return (
    <form
      className={compact ? 'flex flex-wrap items-center gap-2' : 'space-y-2'}
      onSubmit={(e) => {
        e.preventDefault();
        if (!invoice.trim()) return;
        outcome.mutate({ tripId: row.trip_id, status: 'invoiced', invoiceReference: invoice.trim() }, { onSuccess: () => setInvoice('') });
      }}
    >
      <label className={compact ? 'sr-only' : 'block text-xs text-text-muted'} htmlFor={`inv-${row.trip_id}`}>
        Zoho Books invoice number
      </label>
      <div className="flex flex-wrap gap-2">
        <input
          id={`inv-${row.trip_id}`}
          value={invoice}
          onChange={(e) => setInvoice(e.target.value)}
          placeholder="INV-000123"
          autoComplete="off"
          className={`${inputClass} ${compact ? 'w-36' : 'flex-1 min-w-[140px]'}`}
        />
        <button type="submit" disabled={!invoice.trim() || outcome.isPending} className={toneClass.primary}>
          Mark invoiced
        </button>
        <button
          type="button"
          disabled={outcome.isPending}
          className={toneClass.neutral}
          onClick={() => {
            if (window.confirm('Mark this trip not billable?')) outcome.mutate({ tripId: row.trip_id, status: 'not_billable' });
          }}
        >
          Not billable
        </button>
      </div>
      <ErrorLine error={outcome.error} />
    </form>
  );
}
